/**
 * 작성자 : 홍선영
 * 날짜 : 2023.12.13
 * 기능 : 대시보드 컴포넌트 > 유해가스 현황
 */
/**
 * 수정자 : 홍선영
 * 날짜 : 2024.01.11
 * 수정 내용 : 스타일링 및 구조 변경
 */
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v1, v4 } from 'uuid';
import styled from 'styled-components';
// eslint-disable-next-line import/no-extraneous-dependencies
import Carousel from 'react-multi-carousel';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'react-multi-carousel/lib/styles.css';
import { CustomDot } from './Carousel';
import { EndPoint, StartPoint } from '../tunnel/TunnelPoints';
import IssueGuide from '../IssueGuide';
import SkeletonTable from '../SkeletonTable';
import { arraySortByAscdOrder } from '../../utils/arraySortByAscdOrder';
import { ERROR, O2_RANGE } from '../../_constants';
import { apiGet } from '../../services/_common';
import { useQuery } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import { userState } from '../../atoms';
import { ISensorRange } from 'customTypes';
import { convertCO2PpmToPercent, convertCO2PpmToMgPerCubicMeter } from '../../utils/convertCO2Unit';

type Props = {
  size: 'small' | 'large';
  valueType: 'shape' | 'number';
  refetchInterval?: number;
};

// TunnelTable 스타일드 컴포넌트 정의
const MGasTable = styled.div`
  flex-grow: 1;
  display: flex;
  width: 100%;
  overflow: visible;
  .tableHeader,
  .tableBody {
    padding-bottom: 0.75rem;
  }
  .tableHeader {
    padding-left: 0.5rem;
    display: flex;
    flex-direction: column;
    flex: 0 0 9rem;
    > div {
      padding: 0 1rem;
      gap: 0.25rem;
    }
    .sensorName {
      display: flex;
      align-items: center;
      flex: 0 0 3.5rem;
      /* background-color: var(--board); */
      color: var(--text-secondary);
      font-size: 0.875rem;
      .material-symbols-rounded {
        color: var(--text-tertiary);
      }
    }
    .sensors {
      font-size: 0.875rem;
      color: var(--text-secondary);
      padding: 0 0.75rem;
      width: 10rem;
      .counter {
        color: var(--text-tertiary);
        background-color: var(--tonal);
        padding: 0 0.5rem;
        border-radius: 0.5rem;
        margin-right: 0.25rem;
      }
    }
  }
  .tableBody {
    padding-right: 0.5rem;
    display: flex;
    flex-grow: 1;
    overflow: hidden;
    position: relative;
    .carousel-container {
      width: 100%;
    }
    .background-rows {
      position: absolute;
      width: 100%;
      height: 100%;
      padding-right: 0.5rem;
      padding-bottom: 0.75rem;
      pointer-events: none;
    }
    .react-multi-carousel-track {
      height: 100%;
      cursor: grab;
      &:active {
        cursor: grabbing;
      }
      .react-multi-carousel-item {
        display: flex;
        flex-direction: column;
      }
    }
    .area {
      flex: 1 0 8rem;
      display: flex;
      flex-direction: column;
      .areaHeader {
        text-align: center;
        font-size: 0.875rem;
        flex: 0 0 3.5rem;
        color: var(--text-primary);
        .pointLabel {
          display: flex;
        }
      }
      .pointSide {
        display: flex;
        flex: 1 0 10rem;
        .pointDetail {
          flex: 1 0 4rem;
          display: flex;
          flex-direction: column;
          .mgasValue {
            justify-content: center;
          }
        }
      }
    }
  }
  .sensors,
  .mgasValue {
    flex: 1 0 2.125rem;
    display: flex;
    align-items: center;
    border-top: 1px solid var(--outline);
    .removeBackground {
      background-color: transparent !important;
      font-size: 1.375rem !important;
    }
  }
`;

/**
 * 수정자 : 홍선영
 * 날짜 : 2024.10.18
 * 수정 내용 : 무선센서 추가되면서 api요청시 무선/유선 센서 subCd 코드 파라미터추가
 */

// MGasInfoValue 컴포넌트 정의
const MGasInfo = ({ size = 'small', valueType = 'shape', refetchInterval }: Props) => {
  const isSiteDashboard = refetchInterval !== undefined; // 상황판여부
  const { t } = useTranslation();
  const userInfo = useRecoilValue(userState);

  const {
    data: tAreaSensorData,
    isError: isErrorTAreaSensorData,
    isFetching: isFetchingTAreaSensorData,
    refetch: refetchTAreaSensorData,
  } = useQuery(['tareaSensorGet', userInfo.hCd, userInfo.sCd], () => apiGet({ path: '/tarea/t/sensor', req: { hCd: userInfo.hCd, sCd: userInfo.sCd } }), {
    enabled: !!userInfo.hCd && !!userInfo.sCd && userInfo.sCd !== '00000',
    ...(isSiteDashboard && { refetchInterval }),
  });

  const {
    data: sensorUListData,
    isError: isErrorSensorUListData,
    isFetching: isFetchingSensorUListData,
  } = useQuery(['sensorUListGet', userInfo.hCd, userInfo.sCd], () => apiGet({ path: '/sensor/ulist', req: { hCd: userInfo.hCd, sCd: userInfo.sCd, subCd: '08' } }), {
    enabled: !!userInfo.hCd && !!userInfo.sCd && userInfo.sCd !== '00000',
    ...(isSiteDashboard && { refetchInterval }),
  });

  const fetchAllSensorRanges = async () => {
    try {
      const res = await apiGet({ path: '/sensor/range', req: { hCd: userInfo.hCd, sCd: userInfo.sCd } });
      const rangeMap: { [key: string]: { level1: number; level2: number; level3: number; unit?: string } } = {};
      res.data.data.rangeList.forEach((list: ISensorRange) => {
        rangeMap[list.srCd] = {
          level1: list.level1 ? Number(list.level1) : 0,
          level2: list.level2 ? Number(list.level2) : 0,
          level3: list.level3 ? Number(list.level3) : 0,
          unit: list.unit,
        };
      });
      return rangeMap;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const {
    data: allSensorRanges,
    isLoading: isLoadingAllSensorRanges,
    isFetching: isFetchingAllSensorRanges,
  } = useQuery(['allSensorRangesGet', userInfo.hCd, userInfo.sCd], () => fetchAllSensorRanges(), {
    enabled: !!userInfo.hCd && !!userInfo.sCd && userInfo.sCd !== '00000',
    ...(isSiteDashboard && { refetchInterval }),
    cacheTime: 0,
  });

  const [sensorList, setSensorList] = useState<string[]>([]);
  const [mergedData, setMergedData] = useState<MergeType[]>([]);

  // 배열안에 시점과 종점 객체를 병합하는 함수
  const getMergedData = (arr: any[]) => {
    const result: MergeType[] = [];
    arr?.forEach((obj) => {
      const { tatCd, tatGubun, tatName: tatNameTemp, ...rest } = obj;
      const existingItem = result.find((el) => el.tatCd === tatCd);
      const newObject: MergeType = {
        tatCd,
        tatName: tatNameTemp,
        ...(tatGubun === 's' ? { start: { tatGubun, ...rest } } : { end: { tatGubun, ...rest } }),
      };

      if (!existingItem) {
        result.push(newObject);
      } else {
        // 만약 해당 아이템이 존재하면 'start' 또는 'end' 객체 업데이트
        tatGubun === 's' ? (existingItem.start = { tatGubun, ...rest }) : (existingItem.end = { tatGubun, ...rest });
      }
    });
    return result;
  };

  // useEffect: tAreaList 변경 시 mergedData 업데이트
  useEffect(() => {
    if (tAreaSensorData && tAreaSensorData.data.data.tareaList?.length > 0) {
      const data = getMergedData(tAreaSensorData.data.data.tareaList);
      setMergedData(data);
    }
  }, [tAreaSensorData]);

  useEffect(() => {
    if (sensorUListData) {
      const { sensorUList } = sensorUListData.data.data;
      const useYList = sensorUList.filter((el: any) => el.useYn === 'Y' && el.uListYn === 'Y');
      setSensorList(arraySortByAscdOrder(useYList, 'etc1'));
    }
  }, [sensorUListData]);

  // 유해가스목록 갯수에 따라 carousel에 display할 아이템갯수 구하는 함수
  const calculateCarouselItemCount = useCallback(() => {
    if (tAreaSensorData) {
      if (tAreaSensorData.data.data.tareaList?.length <= 2) return tAreaSensorData.data.data.tareaList?.length;
      if (tAreaSensorData.data.data.tareaList?.length <= 4) return 2;
    }
    return 3;
  }, [size, tAreaSensorData]);

  const carouselCount = calculateCarouselItemCount();

  const responsive = useMemo(
    () => ({
      desktop: {
        breakpoint: { max: 3000, min: 1024 },
        items: size === 'large' ? carouselCount : 1,
        slidesToSlide: 1, // optional, default to 1.
      },
      tablet: {
        breakpoint: { max: 1024, min: 464 },
        items: size === 'large' ? 2 : 1,
        slidesToSlide: 1, // optional, default to 1.
      },
      mobile: {
        breakpoint: { max: 464, min: 0 },
        items: 1,
        slidesToSlide: 1, // optional, default to 1.
      },
    }),
    [size, tAreaSensorData]
  );

  // 새로고침 버튼 클릭 시
  const onClickRefresh = () => {
    refetchTAreaSensorData();
  };

  // type 값을 센서 코드로 변환하는 함수
  const getSensorCodeByType = (type: string): string => {
    const typeMap: { [key: string]: string } = {
      '1': 'O2',
      '2': 'NO2',
      '3': 'CO',
      '4': 'CO2',
      '5': 'H2S',
      '6': 'CH4',
      '7': 'CH2O',
      '8': 'O3',
      '9': 'PM2.5',
      '10': 'PM10',
      '11': 'TEMP',
      '12': 'HUMI',
      '13': 'VOC',
      '14': 'PM1',
    };
    return typeMap[type] || '';
  };

  // CO2 ppm 값을 unit에 따라 변환하는 함수
  const convertCO2Value = (ppmValue: number | string | undefined): number => {
    if (ppmValue === undefined || ppmValue === null || ppmValue === '') {
      return 0;
    }
    const numValue = typeof ppmValue === 'string' ? Number(ppmValue) : ppmValue;
    if (isNaN(numValue)) {
      return 0;
    }

    const range = allSensorRanges?.CO2;
    if (!range?.unit) {
      return numValue;
    }

    const { unit } = range;
    switch (unit) {
      case '%':
        return convertCO2PpmToPercent(numValue);
      case 'mg/m³':
      case 'mg':
        return convertCO2PpmToMgPerCubicMeter(numValue);
      case 'ppm':
      default:
        return numValue;
    }
  };

  // 센서 값과 range를 비교하여 level을 계산하는 함수
  const calculateLevel = (sensorCode: string, sensorValue: number | string | undefined): string => {
    if (!allSensorRanges || sensorValue === undefined || sensorValue === null || sensorValue === '') {
      return '';
    }

    const numValue = typeof sensorValue === 'string' ? Number(sensorValue) : sensorValue;
    if (isNaN(numValue)) {
      return '';
    }

    const range = allSensorRanges[sensorCode];
    if (!range || !range.level1 || !range.level2 || !range.level3) {
      return '';
    }

    const { level1, level2, level3 } = range;
    const valueToCompare = Number(numValue);

    // 산소 경우 범위 계산
    if (sensorCode === 'O2') {
      const matchedRange = [...O2_RANGE].reverse().find((o2Range) => {
        const [lowerMin, lowerMax] = o2Range.lower;
        const [upperMin, upperMax] = o2Range.upper;

        // 하한선(lower) 체크: -1은 무시, 값이 범위 내에 있는지 확인
        const isLowerBreached = (lowerMin !== -1 && valueToCompare < lowerMin) || (lowerMax !== -1 && valueToCompare <= lowerMax);

        // 상한선(upper) 체크: -1은 무시
        const isUpperBreached = (upperMin !== -1 && valueToCompare > upperMin) || (upperMax !== -1 && valueToCompare >= upperMax);

        return isLowerBreached || isUpperBreached;
      });

      return matchedRange ? String(matchedRange.level) : '0';
    }

    // CO2의 경우 변환된 값으로 비교
    if (sensorCode === 'CO2' && range.unit) {
      const co2Value = convertCO2Value(numValue);
      if (co2Value < level1) return '0';
      if (co2Value >= level1 && co2Value < level2) return '1';
      if (co2Value >= level2 && co2Value < level3) return '2';
      return '3';
    }

    // level0 (좋음): 값 < level1
    // level1 (주의): level1 <= 값 < level2
    // level2 (경고): level2 <= 값 < level3
    // level3 (위험): level3 <= 값
    if (valueToCompare < level1) {
      return '0';
    }
    if (valueToCompare >= level1 && valueToCompare < level2) {
      return '1';
    }
    if (valueToCompare >= level2 && valueToCompare < level3) {
      return '2';
    }
    return '3';
  };

  const renderList = ({ data, sensorListData, side }: { data: any; sensorListData: any[]; side: string }) => {
    // valueType이 shape 인지 여부
    const valueTypeStatus = valueType === 'shape';
    // valueType이 shape가 아닌 경우 'removeBackground' 반환
    const removeBackground = valueTypeStatus ? 'removeBackground' : '';

    const getValue = (props: any) => {
      // 레벨 값이 0, 1, 2, 3 중 하나인지 여부
      const isShapeValue = (level: string) => ['0', '1', '2', '3'].includes(level);
      // 데이터 값이 NC, null 중 하나인지 여부
      const isDataValue = (dataProp: string | null) => dataProp === 'NC' || dataProp === null;

      // 센서 코드 가져오기
      const sensorCode = getSensorCodeByType(props);

      if (!sensorCode) {
        return '-';
      }

      const dataProp = `data${props}`;
      const dataValue = data[dataProp];

      // valueType이 shape일 때
      if (valueTypeStatus) {
        if (!dataValue || dataValue === 'NC' || dataValue === null || dataValue === '') {
          return '-';
        }

        // 프론트에서 level 계산
        const levelValue = calculateLevel(sensorCode, dataValue);
        return isShapeValue(levelValue) ? '●' : '-';
      }

      // CO2의 경우 단위에 따른 값 변환
      if (sensorCode === 'CO2') {
        return isDataValue(dataValue) ? '-' : Number(convertCO2Value(dataValue)).toLocaleString();
      }

      // valueType이 number일 때
      // 데이터 값이 NC, null일 때 -
      return isDataValue(dataValue) ? '-' : Number(dataValue).toLocaleString();
    };

    return (
      <div className='pointDetail'>
        {sensorListData.map((sensor: any) => {
          const dataProp = `data${sensor.etc1}`;
          const dataValue = data[dataProp];

          // 레벨 값으로 색상 지정
          const getClassName = () => {
            if (!dataValue || dataValue === 'NC' || dataValue === null || dataValue === '') {
              return '';
            }

            // 센서 코드 가져오기
            const sensorCode = getSensorCodeByType(sensor.etc1);
            if (!sensorCode) {
              return '';
            }

            // 프론트에서 level 계산
            const levelValue = calculateLevel(sensorCode, dataValue);

            if (levelValue === '0') return 'blue';
            if (levelValue === '1') return 'green';
            if (levelValue === '2') return 'amber';
            if (levelValue === '3') return 'red';
            return '';
          };

          return (
            <div key={sensor.etc1} className='mgasValue'>
              <span className={`textLight ${getClassName()} ${removeBackground}`}>{getValue(sensor.etc1)}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const loading =
    !tAreaSensorData || !sensorList || !sensorUListData || !allSensorRanges || isFetchingTAreaSensorData || isFetchingSensorUListData || isLoadingAllSensorRanges || isFetchingAllSensorRanges;
  if (isErrorSensorUListData || isErrorTAreaSensorData) return <IssueGuide type='error' text={ERROR} />;

  return (
    <div className='mGas tempEdit stickyHeader' style={{ overflowX: 'hidden' }}>
      <div className='widget-header margin-top'>
        <div className='widget-title flex-between innerBtn'>
          <span>{t('유해가스 현황')}</span>
          <div className='labelSet flex-end'>
            <span className='textBox blue'>{t('좋음')}</span>
            <span className='textBox green'>{t('주의')}</span>
            <span className='textBox amber'>{t('경고')}</span>
            <span className='textBox red'>{t('위험')}</span>
          </div>
          {!isSiteDashboard && (
            <div className='widget-btn-group'>
              <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
                <span className='material-symbols-rounded'>refresh</span>
              </div>
            </div>
          )}
        </div>
      </div>
      {loading ? (
        <div style={{ width: '100%', height: '100%' }}>
          <SkeletonTable />
        </div>
      ) : (
        <MGasTable style={isSiteDashboard && sensorList.length >= 8 ? { animation: 'slide 15s infinite linear' } : { animation: 'none' }}>
          <div className='tableHeader'>
            <div className='sensorName'>
              <span className='material-symbols-rounded'>sensors</span>
              {t('센서명')}
            </div>
            {sensorList.map((el: any, idx: number) => (
              <div className='sensors' key={el.ssCd}>
                <span className='counter'>{idx + 1}</span>
                {el.cdName}
              </div>
            ))}
          </div>
          <div className='tableBody'>
            <Carousel
              swipeable
              draggable
              responsive={responsive}
              infinite
              autoPlay={isSiteDashboard}
              autoPlaySpeed={15000}
              keyBoardControl
              transitionDuration={500}
              containerClass='carousel-container'
              removeArrowOnDeviceType={['tablet', 'mobile', 'desktop']}
              dotListClass='custom-dot-list-style'
              showDots={false}
              customDot={<CustomDot mergedData={mergedData} />}
            >
              {mergedData.map((el: any) => (
                <div key={v1()} className='area'>
                  <div className='areaHeader'>
                    {el.tatName}
                    <div className='pointLabel'>
                      {el.start && <StartPoint />}
                      {el.end && <EndPoint />}
                    </div>
                  </div>
                  <div className='pointSide'>
                    {el.start && renderList({ data: el.start, sensorListData: sensorList, side: 'start' })}
                    {el.end && renderList({ data: el.end, sensorListData: sensorList, side: 'end' })}
                  </div>
                </div>
              ))}
            </Carousel>
            <div className='background-rows area'>
              <div className='areaHeader' />
              <div className='pointSide'>
                <div className='pointDetail'>
                  {sensorList.map(() => (
                    <div className='mgasValue' key={v4()} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </MGasTable>
      )}
    </div>
  );
};

export default MGasInfo;
